import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Snackbar,
  Collapse,
  InputAdornment,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Settings as SettingsIcon,
  Home as HomeIcon,
  Campaign as CampaignIcon,
  Help as HelpIcon,
  Info as InfoIcon,
  AdminPanelSettings as AdminIcon,
  Mic as MicIcon,
  Favorite as FavoriteIcon,
  Description as DescriptionIcon,
  Inbox as InboxIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon2,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CalendarToday as CalendarIcon,
  Info as InfoIcon2,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material';
import { ResponsiveAdminLayout } from '../components/ResponsiveAdminLayout';
import { Pagination } from '../components/Pagination';
import { useAuth } from '../hooks/useAuth';
import { useCompany } from '../hooks/useAuth';
import { ThemeService } from '../services/themeService';
import { Theme } from '../types/theme';
import { AppColors } from '../theme/colors';
import { useIsDesktop } from '../hooks/useMediaQuery';

// Theme型は../types/themeからインポートするため削除

export const ThemeManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { company, loading: companyLoading } = useCompany(user?.uid || null);
  const isDesktop = useIsDesktop();
  
  const [themes, setThemes] = useState<Theme[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSystemAdminExpanded, setIsSystemAdminExpanded] = useState(false);
  const [isCreateThemeExpanded, setIsCreateThemeExpanded] = useState(false);
  const [isArchivedSectionExpanded, setIsArchivedSectionExpanded] = useState(false); // 初期値をfalseに変更
  
  // アーカイブテーマ用のページネーション状態
  const [archivedCurrentPage, setArchivedCurrentPage] = useState(1);
  const archivedItemsPerPage = 10;
  
  // フォーム状態
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // ダイアログ状態
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [unarchiveDialogOpen, setUnarchiveDialogOpen] = useState(false);
  const [targetTheme, setTargetTheme] = useState<Theme | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    if (company?.id) {
      fetchThemes();
    }
  }, [company?.id]);

  const fetchThemes = async () => {
    if (!company?.id) {
      return;
    }
    
    setLoading(true);
    try {
      // 実際のFirestoreからテーマ一覧を取得
      const fetchedThemes = await ThemeService.getThemes(company.id);
      setThemes(fetchedThemes);
    } catch (error) {
      console.error('テーマ取得エラー詳細:', error);
      console.error('エラータイプ:', (error as Error).constructor.name);
      console.error('エラーメッセージ:', (error as Error).message);
      setSnackbar({
        open: true,
        message: `テーマの取得に失敗しました: ${(error as Error).message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      navigate('/admin-login');
    } catch (error) {
      console.error('ログアウトエラー:', error);
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    
    if (!formData.title.trim()) {
      errors.title = 'テーマタイトルを入力してください';
    }
    
    if (!formData.description.trim()) {
      errors.description = 'テーマ説明を入力してください';
    }
    
    if (!formData.startDate) {
      errors.startDate = '開始日を選択してください';
    }
    
    if (!formData.endDate) {
      errors.endDate = '終了日を選択してください';
    }
    
    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      if (endDate <= startDate) {
        errors.endDate = '終了日は開始日より後に設定してください';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !company?.id) return;
    
    setIsSubmitting(true);
    try {
      // 実際のFirestoreにテーマを作成
      const themeId = await ThemeService.createTheme({
        title: formData.title,
        description: formData.description,
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
        companyId: company.id,
      });
      
      
      setSnackbar({
        open: true,
        message: 'テーマを作成しました',
        severity: 'success',
      });
      
      // フォームをクリア
      setFormData({ title: '', description: '', startDate: '', endDate: '' });
      setFormErrors({});
      setIsCreateThemeExpanded(false);
      
      // テーマ一覧を再取得
      await fetchThemes();
    } catch (error) {
      console.error('テーマ作成エラー:', error);
      setSnackbar({
        open: true,
        message: 'テーマの作成に失敗しました',
        severity: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleArchiveClick = (theme: Theme) => {
    setTargetTheme(theme);
    setArchiveDialogOpen(true);
  };

  const handleUnarchiveClick = (theme: Theme) => {
    setTargetTheme(theme);
    setUnarchiveDialogOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (!targetTheme) return;
    
    try {
      // テーマアーカイブ処理
      await ThemeService.archiveTheme(targetTheme.id);
      
      setSnackbar({
        open: true,
        message: 'テーマをアーカイブしました',
        severity: 'success',
      });
      
      // テーマ一覧を再取得
      await fetchThemes();
    } catch (error) {
      console.error('テーマアーカイブエラー:', error);
      setSnackbar({
        open: true,
        message: 'テーマのアーカイブに失敗しました',
        severity: 'error',
      });
    } finally {
      setArchiveDialogOpen(false);
      setTargetTheme(null);
    }
  };

  const handleUnarchiveConfirm = async () => {
    if (!targetTheme) return;
    
    try {
      // テーマ再表示処理
      await ThemeService.unarchiveTheme(targetTheme.id);
      
      setSnackbar({
        open: true,
        message: 'テーマを再表示しました',
        severity: 'success',
      });
      
      // テーマ一覧を再取得
      await fetchThemes();
    } catch (error) {
      console.error('テーマ再表示エラー:', error);
      setSnackbar({
        open: true,
        message: 'テーマの再表示に失敗しました',
        severity: 'error',
      });
    } finally {
      setUnarchiveDialogOpen(false);
      setTargetTheme(null);
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const activeTheme = useMemo(() => {
    const now = new Date();
    return themes.find(theme => {
      if (theme.isArchived === true) return false;
      if (!theme.isActive) return false;
      // 期間チェック: startDate <= now <= endDate
      const started = theme.startDate <= now;
      const notEnded = theme.endDate >= now;
      return started && notEnded;
    });
  }, [themes]);
  const activeThemes = useMemo(() => {
    const filtered = themes.filter(theme => theme.isArchived !== true && theme.isActive !== true);
    // 開始日の新しい順にソート
    return filtered.sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
  }, [themes]);
  const archivedThemes = useMemo(() => {
    const filtered = themes.filter(theme => theme.isArchived === true);
    // 開始日の新しい順にソート
    return filtered.sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
  }, [themes]);
  
  // アーカイブテーマのページネーション計算
  const archivedThemesPaginated = useMemo(() => {
    const startIndex = (archivedCurrentPage - 1) * archivedItemsPerPage;
    const endIndex = startIndex + archivedItemsPerPage;
    return archivedThemes.slice(startIndex, endIndex);
  }, [archivedThemes, archivedCurrentPage, archivedItemsPerPage]);
  
  const totalArchivedPages = Math.ceil(archivedThemes.length / archivedItemsPerPage);

  if (authLoading || companyLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }


  return (
    <ResponsiveAdminLayout pageTitle="テーマ管理">
      <Box>

        {/* 基準の余白の状態: PCは最大幅1000px・中央寄せ */}
        <Box sx={{ maxWidth: isDesktop ? '1000px' : '100%', mx: isDesktop ? 'auto' : 0, width: '100%' }}>
        {/* コンテンツエリア */}
        <Box sx={{ p: 2 }}>
        {/* 今月のテーマを設定・管理 */}
        <Card sx={{ mb: 2.5, borderRadius: 3, border: `1px solid ${AppColors.border}` }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: 'black',
                mb: 2,
              }}
            >
              今月のテーマを設定・管理
            </Typography>
            {activeTheme ? (
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  borderRadius: 2,
                  border: `1px solid ${AppColors.primary}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Chip
                    label="アクティブ"
                    size="small"
                    sx={{
                      bgcolor: AppColors.success,
                      color: 'white',
                      fontSize: 11,
                      height: 20,
                      mr: 1,
                    }}
                  />
                  <Box sx={{ flex: 1 }} />
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: AppColors.textSecondary,
                    }}
                  >
                    投稿数: {activeTheme.statistics.totalPosts}
                  </Typography>
                </Box>
                
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: 'black',
                    mb: 1,
                  }}
                >
                  {activeTheme.title}
                </Typography>
                
                <Typography
                  sx={{
                    fontSize: 12,
                    color: AppColors.textSecondary,
                    mb: 1,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {activeTheme.description}
                </Typography>
                
                <Typography
                  sx={{
                    fontSize: 11,
                    color: AppColors.textTertiary,
                    mb: 1.5,
                  }}
                >
                  期間: {formatDate(activeTheme.startDate)} 〜 {formatDate(activeTheme.endDate)}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  p: 2.5,
                  bgcolor: AppColors.surface,
                  borderRadius: 3,
                  border: `1px solid ${AppColors.border}`,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <InfoIcon2 sx={{ color: AppColors.textSecondary, mr: 1.5 }} />
                <Typography
                  sx={{
                    fontSize: 16,
                    color: AppColors.textSecondary,
                  }}
                >
                  現在アクティブなテーマはありません
                </Typography>
              </Box>
            )}

            {/* 新しいテーマを作成 */}
            <Box sx={{ 
              mt: 3, 
              borderTop: `1px solid ${AppColors.border}`,
              mx: -2.5, // CardContentのパディング分だけ左右に拡張
              px: 2.5,  // 内容は元のパディングを維持
            }}>
              <Box
                onClick={() => setIsCreateThemeExpanded(!isCreateThemeExpanded)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  mb: isCreateThemeExpanded ? 2 : 0,
                  pt: 3,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: 'black',
                  }}
                >
                  新しいテーマを作成
                </Typography>
                <Box sx={{ flex: 1 }} />
                {isCreateThemeExpanded ? <ExpandLessIcon sx={{ color: AppColors.primary }} /> : <ExpandMoreIcon sx={{ color: AppColors.primary }} />}
              </Box>
              
              <Collapse in={isCreateThemeExpanded}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <TextField
                    fullWidth
                    label="テーマタイトル"
                    placeholder="例: 新年度に向けた改善提案"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    error={!!formErrors.title}
                    helperText={formErrors.title}
                    sx={{
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      },
                      '& .MuiOutlinedInput-input': {
                        fontSize: '1rem',
                      },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: AppColors.primary,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: AppColors.primary,
                          borderWidth: 2,
                        },
                      },
                    }}
                  />
                  
                  <TextField
                    fullWidth
                    label="テーマ説明"
                    placeholder="例: 業務改善のアイデアを募集します"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    error={!!formErrors.description}
                    helperText={formErrors.description}
                    multiline
                    rows={3}
                    sx={{
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      },
                      '& .MuiOutlinedInput-input': {
                        fontSize: '1rem',
                      },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: AppColors.primary,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: AppColors.primary,
                          borderWidth: 2,
                        },
                      },
                    }}
                  />
                  
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <TextField
                      fullWidth
                      label="開始日"
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      error={!!formErrors.startDate}
                      helperText={formErrors.startDate}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: <CalendarIcon sx={{ color: AppColors.textSecondary, mr: 1 }} />,
                      }}
                      sx={{
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem',
                          fontWeight: 500,
                        },
                        '& .MuiOutlinedInput-input': {
                          fontSize: '1rem',
                        },
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: AppColors.primary,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: AppColors.primary,
                            borderWidth: 2,
                          },
                        },
                      }}
                    />
                    
                    <TextField
                      fullWidth
                      label="終了日"
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      error={!!formErrors.endDate}
                      helperText={formErrors.endDate}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: <CalendarIcon sx={{ color: AppColors.textSecondary, mr: 1 }} />,
                      }}
                      sx={{
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem',
                          fontWeight: 500,
                        },
                        '& .MuiOutlinedInput-input': {
                          fontSize: '1rem',
                        },
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: AppColors.primary,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: AppColors.primary,
                            borderWidth: 2,
                          },
                        },
                      }}
                    />
                  </Box>
                  
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    sx={{
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${AppColors.primary} 0%, ${AppColors.primaryLight} 100%)`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${AppColors.primaryDark} 0%, ${AppColors.primary} 100%)`,
                      },
                    }}
                  >
                    {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'テーマを作成'}
                  </Button>
                </Box>
              </Collapse>
            </Box>
          </CardContent>
        </Card>

        {/* 小見出し: テーマ一覧 */}
        <Card sx={{ borderRadius: 3, border: `1px solid ${AppColors.border}`, mt: 3 }}>
          <CardContent sx={{ p: 2.5, pb: isArchivedSectionExpanded ? 2.5 : 1.5 }}>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: 'black',
                mb: 2,
              }}
            >
              テーマ一覧
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* アクティブ以外のテーマ */}
            {activeThemes.map((theme) => {
              const now = new Date();
              const isExpired = theme.endDate < now;
              
              return (
                <Box
                  key={theme.id}
                  sx={{
                    p: 2,
                    bgcolor: 'white',
                    borderRadius: 2,
                    border: `1px solid ${theme.isActive ? AppColors.primary : AppColors.border}`,
                  }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      {theme.isActive && (
                        <Chip
                          label="アクティブ"
                          size="small"
                          sx={{
                            bgcolor: AppColors.success,
                            color: 'white',
                            fontSize: 11,
                            height: 20,
                            mr: 1,
                          }}
                        />
                      )}
                      {!theme.isActive && !isExpired && (
                        <Chip
                          label="非アクティブ"
                          size="small"
                          sx={{
                            bgcolor: AppColors.textTertiary,
                            color: 'white',
                            fontSize: 11,
                            height: 20,
                            mr: 1,
                          }}
                        />
                      )}
                      {isExpired && !theme.isActive && (
                        <Chip
                          label="期限切れ"
                          size="small"
                          sx={{
                            bgcolor: AppColors.textTertiary,
                            color: 'white',
                            fontSize: 11,
                            height: 20,
                            mr: 1,
                          }}
                        />
                      )}
                      <Box sx={{ flex: 1 }} />
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: AppColors.textSecondary,
                        }}
                      >
                        投稿数: {theme.statistics.totalPosts}
                      </Typography>
                    </Box>
                    
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 600,
                        color: 'black',
                        mb: 1,
                      }}
                    >
                      {theme.title}
                    </Typography>
                    
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: AppColors.textSecondary,
                        mb: 1,
                    whiteSpace: 'pre-line',
                      }}
                    >
                      {theme.description}
                    </Typography>
                    
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: AppColors.textTertiary,
                        mb: 1.5,
                      }}
                    >
                      期間: {formatDate(theme.startDate)} 〜 {formatDate(theme.endDate)}
                    </Typography>
                    
                    {!theme.isActive && (
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => handleArchiveClick(theme)}
                        sx={{
                          py: 1,
                          fontSize: 12,
                          fontWeight: 600,
                          borderRadius: 2,
                          background: AppColors.primary,
                          '&:hover': {
                            background: AppColors.primaryDark,
                          },
                        }}
                      >
                        アーカイブする
                      </Button>
                    )}
                </Box>
              );
            })}
            
            {/* アーカイブしたテーマセクション */}
            {archivedThemes.length > 0 && (
              <>
                <Box
                  sx={{ 
                    mt: 2, 
                    borderTop: `1px solid ${AppColors.border}`,
                    mx: -2.5, // CardContentのパディング分だけ左右に拡張
                    px: 2.5,  // 内容は元のパディングを維持
                  }}
                >
                  <Box
                    onClick={() => setIsArchivedSectionExpanded(!isArchivedSectionExpanded)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      pt: 3,
                      mb: isArchivedSectionExpanded ? 1.5 : 0.5,
                    }}
                  >
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 600,
                      color: 'black',
                    }}
                  >
                    アーカイブしたテーマ
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  {isArchivedSectionExpanded ? <ExpandLessIcon sx={{ color: AppColors.textSecondary }} /> : <ExpandMoreIcon sx={{ color: AppColors.textSecondary }} />}
                </Box>
                
                <Collapse in={isArchivedSectionExpanded}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {archivedThemesPaginated.map((theme) => (
                      <Box
                        key={theme.id}
                        sx={{
                          p: 2,
                          bgcolor: 'white',
                          borderRadius: 2,
                          border: `1px solid ${AppColors.border}`,
                        }}
                      >
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <Chip
                              label="アーカイブ済み"
                              size="small"
                              sx={{
                                bgcolor: AppColors.textTertiary,
                                color: 'white',
                                fontSize: 11,
                                height: 20,
                                mr: 1,
                              }}
                            />
                            <Box sx={{ flex: 1 }} />
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: AppColors.textSecondary,
                              }}
                            >
                              投稿数: {theme.statistics.totalPosts}
                            </Typography>
                          </Box>
                          
                          <Typography
                            sx={{
                              fontSize: 16,
                              fontWeight: 600,
                              color: 'black',
                              mb: 1,
                            }}
                          >
                            {theme.title}
                          </Typography>
                          
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: AppColors.textSecondary,
                              mb: 1,
                              whiteSpace: 'pre-line',
                            }}
                          >
                            {theme.description}
                          </Typography>
                          
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: AppColors.textTertiary,
                              mb: 1.5,
                            }}
                          >
                            期間: {formatDate(theme.startDate)} 〜 {formatDate(theme.endDate)}
                          </Typography>
                          
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => handleUnarchiveClick(theme)}
                            sx={{
                              py: 1,
                              fontSize: 12,
                              fontWeight: 600,
                              borderRadius: 2,
                              color: AppColors.textSecondary,
                              borderColor: AppColors.border,
                              '&:hover': {
                                borderColor: AppColors.primary,
                                background: AppColors.surface,
                              },
                            }}
                          >
                            再表示
                          </Button>
                      </Box>
                    ))}
                  </Box>
                  
                  {/* アーカイブテーマのページネーション */}
                  {archivedThemes.length > archivedItemsPerPage && (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      py: 3,
                      bgcolor: 'white',
                      borderTop: `1px solid ${AppColors.border}`,
                      mx: -2.5, // CardContentのパディング分だけ左右に拡張
                      px: 2.5,  // 内容は元のパディングを維持
                      mt: 2,    // 上の空白を追加
                    }}>
                      <Typography variant="body2" sx={{ color: AppColors.textSecondary, mb: 2 }}>
                        {archivedThemes.length}件中 {((archivedCurrentPage - 1) * archivedItemsPerPage) + 1}-{Math.min(archivedCurrentPage * archivedItemsPerPage, archivedThemes.length)}件を表示
                      </Typography>
                      <Pagination
                        currentPage={archivedCurrentPage}
                        totalPages={totalArchivedPages}
                        onPageChange={setArchivedCurrentPage}
                      />
                    </Box>
                  )}
                </Collapse>
                </Box>
              </>
            )}
          </Box>
        )}
          </CardContent>
        </Card>
      </Box>
      </Box>

      {/* アーカイブ確認ダイアログ */}
      <Dialog open={archiveDialogOpen} onClose={() => setArchiveDialogOpen(false)}>
        <DialogTitle>テーマをアーカイブ</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {targetTheme?.title} をアーカイブしますか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleArchiveConfirm} color="primary">
            アーカイブ
          </Button>
        </DialogActions>
      </Dialog>

      {/* 再表示確認ダイアログ */}
      <Dialog open={unarchiveDialogOpen} onClose={() => setUnarchiveDialogOpen(false)}>
        <DialogTitle>テーマを再表示</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {targetTheme?.title} を再表示しますか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnarchiveDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleUnarchiveConfirm} color="primary">
            再表示
          </Button>
        </DialogActions>
      </Dialog>

      {/* スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
        </Box>

    </ResponsiveAdminLayout>
  );
};
